import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import apiService from "../utils/apiService";
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Stack,
  Slider,
  TextField,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Snackbar,
  Alert,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import CartellaRegistration from "../components/CartellaRegistration";
import PatternVisualizer from "../components/PatternVisualizer";
import CartellaCheckModal from "../components/CartellaCheckModal";
import SimpleBallDisplay from "../components/SimpleBallDisplay";
import BonusTable from "../components/BonusTable";

import { getRoundNumber, incrementRound } from "../utils/roundManager";
import { debounce } from "lodash";
import "./BingoGame.css";

// Selected cell gradient style - replaces selected.png for better responsiveness
const SELECTED_GRADIENT = "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)";
const SELECTED_STYLE = {
  background: SELECTED_GRADIENT,
  boxShadow: "0 0 10px rgba(255, 215, 0, 0.6), inset 0 0 10px rgba(255, 99, 71, 0.3)",
  border: "2px solid #FFD700"
};

const BingoGame = () => {
  const [numbers] = useState(Array.from({ length: 75 }, (_, i) => i + 1));
  const [shuffledNumbers, setShuffledNumbers] = useState(() => {
    const array = [...numbers];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  });
  const [drawIndex, setDrawIndex] = useState(() => {
    const saved = localStorage.getItem("drawnNumbers");
    if (!saved) return 0;
    try {
      const drawn = JSON.parse(saved);
      return drawn.length;
    } catch {
      localStorage.removeItem("drawnNumbers");
      return 0;
    }
  });
  const [lastDrawn, setLastDrawn] = useState(() => {
    const saved = localStorage.getItem("lastDrawn");
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem("lastDrawn");
      return null;
    }
  });
  const [backgroundStyle, setBackgroundStyle] = useState({});

  // Generate random BINGO numbers for CHEERS bonus
  const [cheersNumbers, setCheersNumbers] = useState(() => {
    const generateRandomBingoNumbers = () => {
      const newNumbers = [];

      // B: 1-15
      newNumbers.push({
        prefix: "B",
        number: Math.floor(Math.random() * 15) + 1,
      });

      // I: 16-30
      newNumbers.push({
        prefix: "I",
        number: Math.floor(Math.random() * 15) + 16,
      });

      // N: 31-45
      newNumbers.push({
        prefix: "N",
        number: Math.floor(Math.random() * 15) + 31,
      });

      // G: 46-60
      newNumbers.push({
        prefix: "G",
        number: Math.floor(Math.random() * 15) + 46,
      });

      // O: 61-75
      newNumbers.push({
        prefix: "O",
        number: Math.floor(Math.random() * 15) + 61,
      });

      return newNumbers;
    };

    return generateRandomBingoNumbers();
  });
  const [showStartModal, setShowStartModal] = useState(() => {
    const gameInProgress = localStorage.getItem("gameInProgress");
    return !gameInProgress;
  });
  const [isDrawing, setIsDrawing] = useState(() => {
    const saved = localStorage.getItem("isDrawing");
    if (!saved) return false;
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem("isDrawing");
      return false;
    }
  });
  const [drawSpeed, setDrawSpeed] = useState(3500); // Default speed: 8.5s per draw (slider value 3.5)
  const [isSliding, setIsSliding] = useState(false);
  const [gameStarted, setGameStarted] = useState(() => {
    const saved = localStorage.getItem("gameStarted");
    if (!saved) return false;
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem("gameStarted");
      return false;
    }
  });
  const [showCartellaRegistration, setShowCartellaRegistration] =
    useState(false);
  const [activeCartellas, setActiveCartellas] = useState(() => {
    const saved = localStorage.getItem("activeCartellas");
    if (!saved) return [];
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem("activeCartellas");
      return [];
    }
  });
  const [gamePattern, setGamePattern] = useState(() => {
    const saved = localStorage.getItem("gamePattern");
    return saved || "Any one Line"; // Set default pattern to 'Any 1 Line'
  });
  const [checkNumber, setCheckNumber] = useState("");
  const checkInputRef = useRef(null);
  const [checkedCartella, setCheckedCartella] = useState(null);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [totalBetAmount, setTotalBetAmount] = useState(() => {
    const saved = localStorage.getItem("totalBetAmount");
    if (!saved) return 0;
    const parsed = parseFloat(saved);
    if (isNaN(parsed)) {
      localStorage.removeItem("totalBetAmount");
      return 0;
    }
    return parsed;
  });
  const [calculationDetails, setCalculationDetails] = useState(() => {
    const saved = localStorage.getItem("calculationDetails");
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem("calculationDetails");
      return null;
    }
  });
  const [currentRound, setCurrentRound] = useState(1);
  const [winningCartella, setWinningCartella] = useState(null);
  const [showWinModal, setShowWinModal] = useState(false);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [selectedCaller, setSelectedCaller] = useState("bereket");
  const [selectedPattern, setSelectedPattern] = useState("oneLine"); // Example state
  const numberAudioRef = useRef({});
  // Profile menu state: open while hovering icon or menu
  const profileIconRef = useRef(null);
  const [isOverIcon, setIsOverIcon] = useState(false);
  const [isOverMenu, setIsOverMenu] = useState(false);
  const hoverCloseTimeout = useRef(null);
  const openProfileMenu = isOverIcon || isOverMenu;
  const handleOpenProfileMenu = () => {
    // controlled by hover booleans
  };
  const handleCloseProfileMenu = () => {
    // close explicitly (e.g., on menu item click)
    setIsOverIcon(false);
    setIsOverMenu(false);
  };
  const handleProfileIconMouseEnter = () => {
    if (hoverCloseTimeout.current) {
      clearTimeout(hoverCloseTimeout.current);
      hoverCloseTimeout.current = null;
    }
    setIsOverIcon(true);
  };
  const handleProfileIconMouseLeave = () => {
    // slight delay to allow cursor to reach the menu without closing
    if (hoverCloseTimeout.current) clearTimeout(hoverCloseTimeout.current);
    hoverCloseTimeout.current = setTimeout(() => {
      setIsOverIcon(false);
      hoverCloseTimeout.current = null;
    }, 180);
  };
  // Change password now handled via dedicated page route
  const handleChangePassword = () => {
    console.log('[BingoGame] Change Password clicked');
    handleCloseProfileMenu();
    navigate('/change-password');
  };

  // Preload all 75 number sounds when caller changes – eliminates audio lag
  useEffect(() => {
    const ext = selectedCaller === 'alex' ? 'mp3' : 'wav';
    const prefix = (n) => (n <= 15 ? 'B' : n <= 30 ? 'I' : n <= 45 ? 'N' : n <= 60 ? 'G' : 'O');
    const cache = {};
    for (let n = 1; n <= 75; n++) {
      const fileName = selectedCaller === 'alex' ? `${n}` : `${prefix(n)}${n}`;
      const key = `${selectedCaller}_${fileName}`;
      const audio = new Audio(`/sounds/${selectedCaller}/${fileName}.${ext}`);
      audio.preload = 'auto';
      cache[key] = audio;
    }
    numberAudioRef.current = cache;
  }, [selectedCaller]);

  // Preload PNG images when component mounts – eliminates image loading lag during animations
  useEffect(() => {
    const preloadImages = async () => {
      const imagesToPreload = [
        '/selected.png',
        '/normal.png',
        '/bingo.png'
      ];
      
      const imagePromises = imagesToPreload.map(src => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
         
            resolve(src);
          };
          img.onerror = () => {
            console.warn(`❌ Failed to preload: ${src}`);
            resolve(src); // Don't reject, just log warning
          };
          img.src = src;
        });
      });
      
      try {
        await Promise.all(imagePromises);
       
      } catch (error) {
        console.warn('Image preloading error:', error);
      }
    };
    
    preloadImages();
  }, []); // Run once on component mount
  const playSound = useCallback(
    async (type, number = null) => {
      if (type === "number" && number) {
        try {
          const key = `${selectedCaller}_${(selectedCaller === 'alex' ? number : `${getPrefix(number)}${number}`)}`;
          const audio = numberAudioRef.current[key];
          if (!audio) return;
          audio.currentTime = 0;
          await audio.play();
          audio.onended = () => {
            audio.currentTime = 0;
          };
        } catch (error) {
          console.warn("Error playing number sound:", error);
        }
      } else if (effectSounds.current[type]) {
        try {
          const audio = effectSounds.current[type];
          audio.currentTime = 0;
          await audio.play();
        } catch (error) {
          console.warn("Error playing effect sound:", error);
        }
      }
    },
    [selectedCaller]
  );

  // Simple audio player
  const callers = useMemo(
    () => [
      { id: "bereket", name: "Bereket" },
      { id: "alex", name: "Alex" },
      { id: "arada", name: "Arada" },
    ],
    []
  );

  // Preload next possible numbers
  const handleCallerChange = useCallback(
    (e) => {
      const newCaller = e.target.value;
      if (newCaller === selectedCaller) return;
      setSelectedCaller(newCaller);
    },
    [selectedCaller]
  );

  const effectSounds = useRef({});
  const [isShuffling, setIsShuffling] = useState(false);
  const [shuffleDisplay, setShuffleDisplay] = useState("--");
  const [shufflingNumbers, setShufflingNumbers] = useState([]);

  // Function to shuffle numbers for a new game
  const shuffleNumbersForNewGame = useCallback(() => {
    const array = [...numbers];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    setShuffledNumbers(array);
    console.log('🎲 Numbers reshuffled for new game!');
  }, [numbers]);

  // Reshuffle numbers when a new game starts (when drawIndex resets to 0)
  useEffect(() => {
    if (drawIndex === 0) {
      shuffleNumbersForNewGame();
    }
  }, [drawIndex, shuffleNumbersForNewGame]);

  // Reset function for new game - can be called from NEW BINGO button
  const resetForNewGame = useCallback(() => {
    shuffleNumbersForNewGame();
    setDrawIndex(0);
    console.log('🆕 Game reset with new random numbers!');
  }, [shuffleNumbersForNewGame]);
  const [recentNumbers, setRecentNumbers] = useState([]);
  const [totalBet, setTotalBet] = useState(0);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [userCut, setUserCut] = useState(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Memoized debounced resize handler
  const handleResize = useCallback(
    debounce(() => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, 200),
    []
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    // Force a resize check on mount (handles DevTools open after load)
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToast(false);
  };

  const getPrefix = (number) => {
    if (number <= 15) return "B";
    if (number <= 30) return "I";
    if (number <= 45) return "N";
    if (number <= 60) return "G";
    return "O";
  };

  const showErrorToast = (message) => {
    setToastMessage(message);
    setOpenToast(true);
  };



  useEffect(() => {
    // Load effect sounds
    const effectFiles = ["bingo", "win", "lose", "start", "shuffle", "passed"];
    for (const effect of effectFiles) {
      try {
        const audio = new Audio(`/sounds/effects/${effect}.wav`);
        audio.preload = "auto";
        effectSounds.current[effect] = audio;
      } catch (error) {
        console.warn(`Error loading effect sound ${effect}:`, error);
      }
    }
    // Load 'paused' sound with .m4a extension
    try {
      const pausedAudio = new Audio(`/sounds/effects/paused.m4a`);
      pausedAudio.preload = "auto";
      effectSounds.current["paused"] = pausedAudio;
    } catch (error) {
      console.warn(`Error loading effect sound paused:`, error);
    }
  }, []);

  const handleCheckCartella = useCallback(async () => {
    const number = parseInt(checkNumber);

    // First, check if the cartella is even in the active game
    const isCartellaActive = activeCartellas.some(
      (c) => c.id === number.toString()
    );
    if (!isCartellaActive) {
      // Play sound immediately - no delays
      const audio = new Audio("/sounds/effects/notreg.wav");
      audio.currentTime = 0;
      audio.play().catch((error) => console.error("Error playing sound:", error));
      
      // Show toast after sound starts
      setTimeout(() => {
        showErrorToast(`Cartella #${number} is not selected for this game.`);
      }, 100);
      return;
    }
    if (isNaN(number)) {
      showErrorToast("Please enter a valid cartella number");
      return;
    }

    try {
      // Fetch the cartella with authentication
      const response = await fetch(`/api/cartellas/${number}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error response:", errorData);
        throw new Error(errorData.error || "Failed to fetch cartella");
      }

      const freshCartella = await response.json();

      // Verify the cartella is from the user's branch
      const userBranchId = JSON.parse(localStorage.getItem("user"))?.branchId;
      if (userBranchId && freshCartella.branchId !== userBranchId) {
        throw new Error("This cartella does not belong to your branch");
      }

      // Update both checked cartella and active cartellas
      setCheckedCartella(freshCartella);
      setActiveCartellas((prev) => {
        const updated = prev.map((c) =>
          c.id === freshCartella.id ? freshCartella : c
        );

        return updated;
      });

      // Check if the cartella has a winning pattern but player called too late
      if (checkWin(freshCartella)) {
        const lastDrawnNumber = shuffledNumbers[drawIndex - 1] || null;
        
        // Helper function to check if a cell is part of the winning pattern
        const isNumberInPattern = (number, rowIndex, colIndex, pattern, cartella) => {
          switch (pattern) {
            case "Any one Line":
            case "Any two Lines": {
              // Check if it's part of a winning row
              const row = cartella.numbers[rowIndex];
              if (row.every(num => num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num))) {
                return true;
              }
              
              // Check if it's part of a winning column
              const column = cartella.numbers.map(r => r[colIndex]);
              if (column.every(num => num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num))) {
                return true;
              }
              
              // Check if it's part of a winning diagonal
              if (rowIndex === colIndex) {
                const mainDiagonal = cartella.numbers.map((r, i) => r[i]);
                if (mainDiagonal.every(num => num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num))) {
                  return true;
                }
              }
              
              if (rowIndex + colIndex === 4) {
                const antiDiagonal = cartella.numbers.map((r, i) => r[4 - i]);
                if (antiDiagonal.every(num => num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num))) {
                  return true;
                }
              }
              
              return false;
            }
            
            case "T Pattern":
              return rowIndex === 0 || colIndex === 2;
              
            case "Reverse T":
              return rowIndex === 4 || colIndex === 2;
              
            case "X Pattern":
              return rowIndex === colIndex || rowIndex + colIndex === 4;
              
            case "L Pattern":
              return rowIndex === 4 || colIndex === 0;
              
            case "Reverse L":
              return rowIndex === 4 || colIndex === 4;
              
            case "4 Corners Square":
              return (rowIndex === 0 && (colIndex === 0 || colIndex === 4)) ||
                     (rowIndex === 4 && (colIndex === 0 || colIndex === 4));
              
            case "Half Above":
              return rowIndex < 3;
              
            case "Half Below":
              return rowIndex >= 2;
              
            case "Half Left":
              return colIndex < 3;
              
            case "Half Right":
              return colIndex >= 2;
              
            case "G and O":
              return colIndex === 3 || colIndex === 4;
              
            case "B and O":
              return colIndex === 0 || colIndex === 4;
              
            case "Mark":
              return (rowIndex === 0 || rowIndex === 4) || (colIndex === 0 || colIndex === 4);
              
            case "T Cross":
              return rowIndex === 2 || colIndex === 2;
              
            default:
              return false;
          }
        };
        
        // Get all numbers that are part of the current winning pattern
        const getPatternNumbers = (cartella, pattern) => {
          const patternNumbers = [];
          
          for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
              const cellNumber = cartella.numbers[i][j];
              if (cellNumber && cellNumber !== "free") {
                // Check if this cell is part of the winning pattern
                const isPartOfPattern = isNumberInPattern(cellNumber, i, j, pattern, cartella);
                if (isPartOfPattern) {
                  patternNumbers.push(cellNumber);
                }
              }
            }
          }
          
          return patternNumbers;
        };
        
        const winningNumbers = getPatternNumbers(freshCartella, gamePattern);
        
        
        
        // Passed sound logic moved to CartellaCheckModal for better control
      }

      setShowCheckModal(true);
      setCheckNumber(""); // Clear the input
    } catch (err) {
      console.error("Error fetching fresh cartella data:", err);
      showErrorToast("Error checking cartella. Please try again.");
    }
  }, [activeCartellas, checkNumber, drawIndex, shuffledNumbers, showErrorToast]);

  const handleCheckInputKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleCheckCartella();
      } else if (e.key === "Backspace" && checkNumber === "") {
        // When backspace is pressed on empty input, close modal
        setShowCheckModal(false);
      }
    },
    [handleCheckCartella, checkNumber]
  );

  const playNumberSound = useCallback(
    (number) => {
      playSound("number", number);
    },
    [playSound]
  );

  useEffect(() => {
    if (!gameStarted || !isDrawing) return;

    let interval;
    const drawNumber = async () => {
      try {
        if (drawIndex < numbers.length) {
          // Increment draw index to get the next pre-shuffled number
          setDrawIndex((prev) => {
            const newIndex = prev + 1;
            if (newIndex <= numbers.length) {
              const newNumber = shuffledNumbers[newIndex - 1];
              playNumberSound(newNumber);
              // Check for wins only to show modal, but don't pause the game
              if (activeCartellas.length > 0) {
                activeCartellas.forEach((cartella) => {
                  if (checkWin(cartella)) {
                    setWinningCartella(cartella);
                    setShowWinModal(true);
                   
                  }
                });
              }
            }
            return newIndex;
          });
        } else {
          // Game over
          setIsDrawing(false);
         
          localStorage.setItem("isDrawing", JSON.stringify(false));
        }
      } catch (error) {
        console.error("Error drawing number:", error);
      }
    };

    if (isDrawing) {
      interval = setInterval(drawNumber, drawSpeed);
    }

    return () => clearInterval(interval);
  }, [isDrawing, drawSpeed, gameStarted, numbers.length, activeCartellas, shuffledNumbers, drawIndex, playNumberSound]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCloseModal = () => {
    if (!calculationDetails) {
      showErrorToast("Please register cards first");
      return;
    }
    if (activeCartellas.length > 0) {
      setShowStartModal(false);
    }
  };

  const handleStartGame = async () => {
    try {
      if (!calculationDetails) {
        throw new Error("please register cards");
      }

      // Play start sound
      playSound("start");

      // Calculate the total amount to deduct (bet amount)
      const totalDeduction =
        calculationDetails.betPerCartella * activeCartellas.length;

      // Calculate the cut amount using user's actual cut percentage
      const cutAmount = totalDeduction * ((userCut || 20) / 100); // Fallback to 20% if userCut is not loaded

      // Place bet and deduct only the cut amount
      const payload = {






        selectedCartellas: activeCartellas,
      betAmount: calculationDetails.betPerCartella, // This is the cut amount, not the bet per cartella
        pattern: gamePattern,
        totalBet: totalDeduction, // This is the full bet amount
        cutAmount: cutAmount,
        adjustedTotalBet: totalDeduction - cutAmount,
    };

  

    // Place bet and deduct only the cut amount
    const response = await fetch('/api/cartellas/place-bet', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const data = await response.json();
      if (data.details) {
        console.error('--- Insufficient Funds Debug Info ---');
 
      }
      throw new Error(data.message || data.error || "Failed to place bet");
    }

      const data = await response.json();
      setRecentNumbers([]); // Reset recent numbers
      setGameStarted(true);
      setShowStartModal(false);
      setDrawIndex(0);
      setIsDrawing(false);
      if (calculationDetails) {
        setTotalBetAmount(calculationDetails.adjustedTotalBet); // Set to current win amount
      }
    } catch (error) {
      console.error("Error starting game:", error);
      alert(
        error.message || "Failed to start game. Please check your balance."
      );
    }
  };

  const handleNewBingo = async () => {
    if (!calculationDetails) {
      showErrorToast("Please register cards first");
      return;
    }

    try {
      // Save round data
      const response = await fetch("/api/reports/save-round", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          round: currentRound,
          price: calculationDetails.betPerCartella,
          noPlayer: calculationDetails.numberOfCartellas,
          winnerPrice: calculationDetails.adjustedTotalBet,
          income: calculationDetails.cutAmount,
          date: new Date().toISOString(),
          branchId: user.branchId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save round data");
      }

      // Increment the round for the next game, after saving the report
      await incrementRound();

      // Generate new CHEERS numbers for the next game
      setCheersNumbers(() => {
        const newNumbers = [];
        newNumbers.push({
          prefix: "B",
          number: Math.floor(Math.random() * 15) + 1,
        });
        newNumbers.push({
          prefix: "I",
          number: Math.floor(Math.random() * 15) + 16,
        });
        newNumbers.push({
          prefix: "N",
          number: Math.floor(Math.random() * 15) + 31,
        });
        newNumbers.push({
          prefix: "G",
          number: Math.floor(Math.random() * 15) + 46,
        });
        newNumbers.push({
          prefix: "O",
          number: Math.floor(Math.random() * 15) + 61,
        });
        return newNumbers;
      });

      // Reset game state
      setDrawIndex(0);
      setIsDrawing(false);
      setGameStarted(false);
      setShowCheckModal(false);
      setShowStartModal(true);
      setActiveCartellas([]);
      setCalculationDetails(null);
      setTotalBetAmount(0); // Reset totalBetAmount
      setRecentNumbers([]);
      setGamePattern("Any one Line");

      // Clear local storage
      localStorage.removeItem("drawnNumbers");
      localStorage.removeItem("lastDrawn");
      localStorage.removeItem("gameStarted");
      localStorage.removeItem("gameInProgress");
      localStorage.removeItem("activeCartellas");
      localStorage.removeItem("totalBetAmount");
      localStorage.removeItem("gamePattern");
      localStorage.removeItem("calculationDetails");

      // Reset all cell styles
      numbers.forEach((num) => {
        const element = document.getElementById(`cell-${num}`);
        if (element) {
          element.style.transition = "none";
          element.style.background = `linear-gradient(rgba(128, 128, 128, 0.08), rgba(128, 128, 128, 0.08)), url(/normal.png)`;
          element.style.backgroundSize = "cover";
          element.style.backgroundPosition = "center";
          element.style.color = "gray";
          element.style.zIndex = 1;
        }
      });
    } catch (error) {
      console.error("Error starting new game:", error);
      showErrorToast("Failed to start new game. Please try again.");
    }
  };

  const handleShuffle = () => {
    if (isShuffling) return;
    playSound("shuffle");
    setIsShuffling(true);

    const startTime = Date.now();
    const SHUFFLE_DURATION = 9000; // 9 seconds

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime >= SHUFFLE_DURATION) {
        clearInterval(interval);
        setIsShuffling(false);
        setShufflingNumbers([]);
        return;
      }

      // Generate 6 random numbers for this iteration
      const newShuffleNumbers = Array.from(
        { length: 6 },
        () => Math.floor(Math.random() * 75) + 1
      );
      setShufflingNumbers(newShuffleNumbers);
    }, 300); // Change numbers every 300ms
  };

  const toggleDrawing = () => {
    const newIsDrawing = !isDrawing;
    setIsDrawing(newIsDrawing);
    if (!newIsDrawing) {
      setTimeout(() => {
        playSound("paused");
      }, 500); // Delay of 1 second before playing the sound
    }
  };

  // Space key event listener for pause/resume
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.code === 'Space' && gameStarted) {
        event.preventDefault(); // Prevent page scroll
        toggleDrawing();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [gameStarted, toggleDrawing]);

  const handleSpeedChange = (_, newValue) => {
    // Convert slider value (2-10) to milliseconds (10000-2000)
    // Now supports decimal values like 3.5 for more precise control
    setDrawSpeed((12 - newValue) * 1000);
  };

  const handleCartellaSelect = async ({
    cartellas,
    pattern,
    betAmount,
    totalBet,
    calculationDetails,
  }) => {
    try {
      // First update the state
      setActiveCartellas(cartellas);
      setGamePattern(pattern);
      setTotalBetAmount(calculationDetails.adjustedTotalBet);
      setCalculationDetails(calculationDetails);
      setShowCartellaRegistration(false);
    } catch (error) {
      console.error("Error in handleCartellaSelect:", error);
      setShowCartellaRegistration(true); // Show registration again on error
      throw error;
    }
  };

  const bingoLetters = ["B", "I", "N", "G", "O"];

  const organizedNumbers = [];
  for (let i = 0; i < 75; i += 15) {
    organizedNumbers.push(numbers.slice(i, i + 15));
  }

  const checkWin = (cartella) => {
    if (!cartella || !cartella.numbers || !gamePattern) return false;

    const checkLine = (numbers) =>
      numbers.every((num) => num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num));
    const getColumn = (colIndex) =>
      cartella.numbers.map((row) => row[colIndex]);
    const getDiagonal = (reverse = false) => {
      return cartella.numbers.map((row, i) => row[reverse ? 4 - i : i]);
    };

    switch (gamePattern) {
      case "Any one Line": {
        // Check rows
        if (cartella.numbers.some((row) => checkLine(row))) return true;
        // Check columns
        for (let col = 0; col < 5; col++) {
          if (checkLine(getColumn(col))) return true;
        }
        // Check diagonals
        if (checkLine(getDiagonal()) || checkLine(getDiagonal(true)))
          return true;
        // Check 4 corner squares
        const fourCorners = [
          cartella.numbers[0][0], // Top-left
          cartella.numbers[0][4], // Top-right
          cartella.numbers[4][0], // Bottom-left
          cartella.numbers[4][4]  // Bottom-right
        ];
        if (checkLine(fourCorners)) return true;
        return false;
      }

      case "Any two Lines": {
        let lineCount = 0;
        // Check rows
        lineCount += cartella.numbers.filter((row) => checkLine(row)).length;
        // Check columns
        for (let col = 0; col < 5; col++) {
          if (checkLine(getColumn(col))) lineCount++;
        }
        // Check diagonals
        if (checkLine(getDiagonal())) lineCount++;
        if (checkLine(getDiagonal(true))) lineCount++;
        return lineCount >= 2;
      }

      case "T Pattern":
        return (
          checkLine(cartella.numbers[0]) && // Top row
          checkLine(getColumn(2))
        ); // Middle column

      case "Reverse T":
        return (
          checkLine(cartella.numbers[4]) && // Bottom row
          checkLine(getColumn(2))
        ); // Middle column

      case "X Pattern":
        return checkLine(getDiagonal()) && checkLine(getDiagonal(true));

      case "L Pattern":
        return (
          checkLine(cartella.numbers[4]) && // Bottom row
          checkLine(getColumn(0))
        ); // Left column

      case "Reverse L":
        return (
          checkLine(cartella.numbers[4]) && // Bottom row
          checkLine(getColumn(4))
        ); // Right column

      case "Half Above":
        return cartella.numbers.slice(0, 3).every((row) => checkLine(row));

      case "Half Below":
        return cartella.numbers.slice(2).every((row) => checkLine(row));

      case "Half Left":
        return [0, 1, 2].every((col) => checkLine(getColumn(col)));

      case "Half Right":
        return [2, 3, 4].every((col) => checkLine(getColumn(col)));

      case "G and O":
        return checkLine(getColumn(3)) && checkLine(getColumn(4));

      case "B and O":
        return checkLine(getColumn(0)) && checkLine(getColumn(4));

      case "Mark":
        return (
          checkLine(cartella.numbers[0]) && // Top row
          checkLine(cartella.numbers[4]) && // Bottom row
          checkLine(getColumn(0)) && // Left column
          checkLine(getColumn(4))
        ); // Right column

      case "T Cross":
        return (
          checkLine(cartella.numbers[2]) && // Middle row
          checkLine(getColumn(2))
        ); // Middle column

      case "4 Corners Square": {
        // Check all four corners: (0,0), (0,4), (4,0), (4,4)
        const corners = [
          cartella.numbers[0][0], // Top-left
          cartella.numbers[0][4], // Top-right
          cartella.numbers[4][0], // Bottom-left
          cartella.numbers[4][4]  // Bottom-right
        ];
        return corners.every(num => 
          num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num)
        );
      }

      default:
        return false;
    }
  };

  const getWinningNumbers = (cartella) => {
    if (!cartella || !cartella.numbers || !gamePattern) return [];

    const checkLine = (numbers) =>
      numbers.every((num) => num === "free" || shuffledNumbers.slice(0, drawIndex).includes(num));
    const getColumn = (colIndex) =>
      cartella.numbers.map((row) => row[colIndex]);
    const getDiagonal = (reverse = false) => {
      return cartella.numbers.map((row, i) => row[reverse ? 4 - i : i]);
    };

    let winningNumbers = [];

    switch (gamePattern) {
      case "Any one Line": {
        // Check rows
        for (const row of cartella.numbers) {
          if (checkLine(row)) {
            winningNumbers = [...row.filter(num => num !== "free")];
            break;
          }
        }
        // Check columns if no winning row found
        if (winningNumbers.length === 0) {
          for (let col = 0; col < 5; col++) {
            const column = getColumn(col);
            if (checkLine(column)) {
              winningNumbers = [...column.filter(num => num !== "free")];
              break;
            }
          }
        }
        // Check diagonals if no winning line found yet
        if (winningNumbers.length === 0) {
          const diagonal1 = getDiagonal();
          if (checkLine(diagonal1)) {
            winningNumbers = [...diagonal1.filter(num => num !== "free")];
          } else {
            const diagonal2 = getDiagonal(true);
            if (checkLine(diagonal2)) {
              winningNumbers = [...diagonal2.filter(num => num !== "free")];
            }
          }
        }
        break;
      }
      // Add other pattern cases as needed
      default:
        break;
    }

    return winningNumbers;
  };

  const refreshCartellaData = async () => {
    try {
      // If we have a checked cartella, refresh its data
      if (checkedCartella) {
        const response = await fetch(`/api/cartellas/${checkedCartella.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch cartella");
        }
        const updatedCartella = await response.json();
        setCheckedCartella(updatedCartella);

        // Also update the cartella in activeCartellas
        setActiveCartellas((prev) =>
          prev.map((cartella) =>
            cartella.id === updatedCartella.id ? updatedCartella : cartella
          )
        );
      }
    } catch (err) {
      console.error("Error refreshing cartella data:", err);
    }
  };

  // Debounced localStorage persistence
  const persistGameState = useRef(
    debounce((state) => {
      localStorage.setItem("gameStarted", JSON.stringify(state.gameStarted));
      if (state.gameStarted) {
        localStorage.setItem("gameInProgress", "true");
      } else {
        localStorage.removeItem("gameInProgress");
      }
      localStorage.setItem("drawnNumbers", JSON.stringify(state.drawnNumbers));
      localStorage.setItem("lastDrawn", JSON.stringify(state.lastDrawn));
      localStorage.setItem("isDrawing", JSON.stringify(state.isDrawing));
      localStorage.setItem(
        "activeCartellas",
        JSON.stringify(state.activeCartellas)
      );
      localStorage.setItem("totalBetAmount", state.totalBetAmount);
      localStorage.setItem("gamePattern", state.gamePattern);
      localStorage.setItem(
        "calculationDetails",
        state.calculationDetails ? JSON.stringify(state.calculationDetails) : ""
      );
    }, 300)
  ).current;

  useEffect(() => {
    persistGameState({
      gameStarted,
      drawnNumbers: shuffledNumbers.slice(0, drawIndex),
      lastDrawn: shuffledNumbers[drawIndex - 1] || null,
      isDrawing,
      activeCartellas,
      totalBetAmount,
      gamePattern,
      calculationDetails,
    });
  }, [
    gameStarted,
    drawIndex,
    isDrawing,
    activeCartellas,
    totalBetAmount,
    gamePattern,
    calculationDetails,
  ]);

  // Optimize fetchActiveCartellas effect
  useEffect(() => {
    if (!showCheckModal || !activeCartellas.length) return;
    const fetchActiveCartellas = async () => {
      try {
        const promises = activeCartellas.map((cartella) =>
          fetch(`/api/cartellas/${cartella.id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }).then((res) => res.json())
        );
        const updatedCartellas = await Promise.all(promises);
        setActiveCartellas(updatedCartellas);
      } catch (err) {
        console.error("Error refreshing active cartellas:", err);
      }
    };
    fetchActiveCartellas();
  }, [showCheckModal, activeCartellas]);

  const fetchCurrentRound = useCallback(async () => {
    try {
      const response = await fetch(`/api/rounds/current/${user.branchId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch current round');
      const data = await response.json();
      const serverRound = data.round || 1;
      const lastUpdateDate = data.lastUpdateDate || new Date().toISOString().split('T')[0];
      const today = new Date().toISOString().split('T')[0];
      const savedRound = localStorage.getItem(`currentRound_${user.branchId}_${user.id}`);

      

      if (lastUpdateDate !== today) {
       
        await fetch('/api/rounds/reset', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ branchId: user.branchId })
        });
        setCurrentRound(1);
        localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, '1');
        localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
      } else if (serverRound < parseInt(savedRound || '0', 10)) {

        setCurrentRound(parseInt(savedRound, 10));
        // Optionally, update the server with the correct round
        fetch(`/api/rounds/set/${user.branchId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ branchId: user.branchId, roundNumber: parseInt(savedRound, 10) })
        }).catch(err => console.error('Failed to update server round:', err));
      } else if (serverRound !== parseInt(savedRound || '0', 10)) {
       
        setCurrentRound(serverRound);
        localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, serverRound.toString());
        localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
      } else {
        setCurrentRound(serverRound);
      }
    } catch (error) {
      console.error('Error fetching current round:', error);
      const savedRound = localStorage.getItem(`currentRound_${user.branchId}_${user.id}`);
      const savedDate = localStorage.getItem(`roundDate_${user.branchId}_${user.id}`);
      const today = new Date().toISOString().split('T')[0];

      if (savedDate !== today) {
       
        setCurrentRound(1);
        localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, '1');
        localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
      } else if (savedRound) {
      
        setCurrentRound(parseInt(savedRound, 10));
      } else {
       
        setCurrentRound(1);
      }
    }
  }, [user.branchId, user.id]);

  useEffect(() => {
    if (user && user.branchId && user.id) {
      fetchCurrentRound();
    }
  }, [user, fetchCurrentRound]);

  useEffect(() => {
    const savedDate = localStorage.getItem(`roundDate_${user.branchId}_${user.id}`);
    const today = new Date().toISOString().split('T')[0];

    if (savedDate !== today) {
      localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, '1');
      localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
      setCurrentRound(1);
    } else {
      const savedRound = localStorage.getItem(`currentRound_${user.branchId}_${user.id}`);
      if (savedRound) {
        setCurrentRound(parseInt(savedRound, 10));
      }
      fetchCurrentRound();
    }
  }, [fetchCurrentRound, user.branchId, user.id]);

  const incrementRound = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const savedDate = localStorage.getItem(`roundDate_${user.branchId}_${user.id}`);

      if (savedDate !== today) {
        
        // Reset round to 1 if it's a new day
        await fetch('/api/rounds/reset', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ branchId: user.branchId })
        });
        setCurrentRound(1);
        localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, '1');
        localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
        return;
      }

      const response = await fetch(`/api/rounds/increment/${user.branchId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ branchId: user.branchId })
      });
      if (!response.ok) throw new Error('Failed to increment round');
      const data = await response.json();
    
      setCurrentRound(data.currentRound);
      localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, data.currentRound.toString());
      localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
    } catch (error) {
      console.error('Error incrementing round:', error);
      const today = new Date().toISOString().split('T')[0];
      const savedDate = localStorage.getItem(`roundDate_${user.branchId}_${user.id}`);

      if (savedDate !== today) {
       
        setCurrentRound(1);
        localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, '1');
        localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
      } else {
        const savedRound = localStorage.getItem(`currentRound_${user.branchId}_${user.id}`);
        const newRound = savedRound ? parseInt(savedRound, 10) + 1 : 2;
        
        setCurrentRound(newRound);
        localStorage.setItem(`currentRound_${user.branchId}_${user.id}`, newRound.toString());
        localStorage.setItem(`roundDate_${user.branchId}_${user.id}`, today);
      }
    }
  }, [user.branchId, user.id]);

  const currentDrawnNumbers = useMemo(() => {
    return shuffledNumbers.slice(0, drawIndex);
  }, [shuffledNumbers, drawIndex]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: "100vh",
        width: "100vw",
        backgroundColor: "#1a1a1a",
        overflow: "hidden",
      }}
    >
      <Dialog
        open={showStartModal}
        onClose={() => {}}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#f5f5f5",
            overflow: "visible",
            minWidth: "600px",
            height: "auto",
            maxHeight: "none",
            opacity: 0.9,
            m: 1,
            borderRadius: 0,
            top: -60,
            left: 0,
            right: 0,
            bottom: "auto",
            "& .MuiDialogContent-root": {
              overflow: "visible",
              padding: 0,
            },
          },
        }}
      >
        {/* Header with Register/Logout and Round */}
        <Box
          sx={{
            bgcolor: "#242424",
            py: 1,
            px: 2,
            opacity: 0.9,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Left side - Register/Logout/Report */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              onClick={handleLogout}
              sx={{
                color: "white",
                fontSize: "1.2rem",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Logout
            </Typography>
            <Typography sx={{ color: "white", fontSize: "1.2rem" }}>
              /
            </Typography>
            <Typography
              onClick={() => setShowCartellaRegistration(true)}
              sx={{
                color: "white",
                fontSize: "1.2rem",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Register Card
            </Typography>
            <Typography sx={{ color: "white", fontSize: "1.2rem" }}>
              /
            </Typography>
            <Typography
              onClick={() => navigate("/report")}
              sx={{
                color: "white",
                fontSize: "1.2rem",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Report
            </Typography>
          </Box>

          {/* Right side - Round count */}
          <Typography
            sx={{
              color: "white",
              fontSize: "1.2rem",
            }}
          >
            Round {currentRound}
          </Typography>
        </Box>

        <DialogContent sx={{ bgcolor: "#f5f5f5", p: 2 }}>
          {/* Amharic Text */}
          <Typography
            align='center'
            sx={{
              // mb: 2,
              fontSize: "1.9rem",
              color: "#666",
              fontWeight: "bold",
            }}
          >
            የጨዋታው ትእዛዝ
          </Typography>

          {/* Cartella Grid with BINGO header */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              mx: "auto",
              mb: 2,
            }}
          >
            {/* Numbers Grid replaced with image */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src='/bing.png'
                alt='Bingo Board'
                style={{
                  width: "100%",
                  maxWidth: 300,
                  height: "auto",
                  borderRadius: 8,
                }}
              />
            </Box>




            {/* Bet Amount in Amharic */}
            {calculationDetails && (
              <Typography
                align='center'
                sx={{
                  fontSize: "4rem",
                  color: "#666",
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                }}
              >
                {calculationDetails.adjustedTotalBet} ወሳጅ
              </Typography>
            )}

            {/* Start Button */}
            <Box sx={{ display: "flex", justifyContent: "start" }}>
              <Button
                variant='contained'
                onClick={handleStartGame}
                sx={{
                  minWidth: 220,
                  maxHeight: "40px",
                  py: 1,
                  mt: 1,
                  fontSize: "1.2rem",
                  backgroundColor: "#800000",
                  "&:hover": {
                    backgroundColor: "#600000",
                  },
                  borderRadius: 0,
                }}
              >
                Start
              </Button>
            </Box>
          </Box>

          {/* Start Button */}
        </DialogContent>
      </Dialog>

      <CartellaRegistration
        open={showCartellaRegistration}
        onClose={() => setShowCartellaRegistration(false)}
        onSelect={handleCartellaSelect}
        currentRound={currentRound}
        onCartellaUpdate={refreshCartellaData}
      />

      <Box
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "0", // Allows the parent to shrink
        }}
      >
        {/* Top section - all boxes */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: { xs: "auto", md: "340px" },
            width: "100%",
            backgroundColor: "#1a1a1a",
            mb: 0,
            p: 0,
          }}
        >
          {/* Lottery Drum display */}
          <Box
            sx={{
              width: { xs: "100%", md: "15%" },
              height: "90%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <SimpleBallDisplay
              lastDrawn={shuffledNumbers[drawIndex - 1] || null}
              isDrawing={isDrawing}
              isShuffling={isShuffling}
              shuffleDisplay={shuffleDisplay}
              getPrefix={getPrefix}
            />
          </Box>

          {/* Glass tube as part of the lottery drum with recent balls */}
          <Box
            sx={{
              width: { xs: "100%", md: "25%" },
              height: "95%",
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "flex-start",
              position: "relative",
              ml: -1,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "80px",
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0.5), rgba(255,255,255,0.2))",
                borderRadius: "0 35px 35px 0",
                border: "3px solid rgba(255,255,255,0.5)",
                borderLeft: "none",
                boxShadow:
                  "inset 0 0 20px rgba(255,255,255,0.3), 0 5px 15px rgba(0,0,0,0.5)",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-around",
                paddingLeft: "10%",
                paddingRight: "5%",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "12px",
                  bottom: "12px",
                  left: "10px",
                  right: "60%",
                  background:
                    "linear-gradient(to right, rgba(255,255,255,0.3), rgba(255,255,255,0))",
                  borderRadius: "0 35px 35px 0",
                  zIndex: 0,
                },
              }}
            >
              {/* Real drawn numbers from the game state - in reverse order to show most recent first */}
              {[...currentDrawnNumbers]
                .reverse()
                .slice(0, 5)
                .map((number, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: { xs: 40, sm: 45, md: 50 },
                      height: { xs: 40, sm: 45, md: 50 },
                      borderRadius: "50%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%) !important",
                      boxShadow: index === 0
                        ? "0 0 15px rgba(255,255,255,0.8), 0 0 10px rgba(255, 215, 0, 0.6), inset 0 0 10px rgba(255, 99, 71, 0.3)"
                        : "0 4px 8px rgba(0,0,0,0.3), 0 0 10px rgba(255, 215, 0, 0.6), inset 0 0 10px rgba(255, 99, 71, 0.3)",
                      border: "2px solid #FFD700 !important",
                      color: "#444444",
                      zIndex: 5 - index,
                      opacity: 1 - index * 0.15,
                      transform: `scale(${1 - index * 0.1})`,
                      animation:
                        index === 0 ? "newBallPulse 1.5s infinite" : "none",
                      "@keyframes newBallPulse": {
                        "0%": { boxShadow: "0 0 15px rgba(255,255,255,0.7)" },
                        "50%": { boxShadow: "0 0 25px rgba(255,255,255,1)" },
                        "100%": { boxShadow: "0 0 15px rgba(255,255,255,0.7)" },
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                        fontWeight: "bold",
                        color: "#444444",
                        mb: -0.5,
                      }}
                    >
                      {getPrefix(number)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                        fontWeight: "bolder",
                        color: "#444444",
                      }}
                    >
                      {number}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>

          {/* Profile + Balance icons (hover menu, larger, lower, no box styling) */}
          <Box
            sx={{
              width: { xs: "100%", md: "10%" },
              height: "65%",
              display: { xs: "flex", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mr: 7,
              zIndex: 5,
              mt: 6, // lower the icons more
            }}
          >
            <Tooltip
              title={`Balance: ${Number((user && user.credits) || 0).toLocaleString('en-US')} Birr`}
              placement="bottom"
              arrow
            >
              <IconButton size="large" sx={{ color: 'white' }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 34 }} />
              </IconButton>
            </Tooltip>
            <IconButton
              size="large"
              sx={{ color: 'white' }}
              ref={profileIconRef}
              onMouseEnter={handleProfileIconMouseEnter}
              onMouseLeave={handleProfileIconMouseLeave}
            >
              <AccountCircleIcon sx={{ fontSize: 36 }} />
            </IconButton>
            <Menu
              anchorEl={profileIconRef.current}
              open={openProfileMenu}
              onClose={handleCloseProfileMenu}
              keepMounted
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{
                // Keep open while hovering menu; close when leaving both icon and menu
                onMouseEnter: () => {
                  if (hoverCloseTimeout.current) {
                    clearTimeout(hoverCloseTimeout.current);
                    hoverCloseTimeout.current = null;
                  }
                  setIsOverMenu(true);
                },
                onMouseLeave: () => {
                  setIsOverMenu(false);
                  if (hoverCloseTimeout.current) clearTimeout(hoverCloseTimeout.current);
                  hoverCloseTimeout.current = setTimeout(() => {
                    // if also not over icon, menu will close because open=false
                    hoverCloseTimeout.current = null;
                  }, 120);
                },
              }}
            >
              <MenuItem onClick={handleChangePassword}>Change Password</MenuItem>
              <MenuItem onClick={() => { handleCloseProfileMenu(); handleLogout(); }}>
                <LogoutIcon fontSize="small" style={{ marginRight: 8 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>

          {/* Pattern display */}
          <Box
            sx={{
              width: { xs: "100%", md: "16%" },
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "visible",
              borderRight: "1px solid rgba(255,255,255,0.1)",
              position: "relative",
              mr: 0,
              ml: 1,
            }}
          >
            <PatternVisualizer
              pattern={gamePattern}
              gameStarted={gameStarted}
              lastDrawn={shuffledNumbers[drawIndex - 1] || null}
            />
          </Box>

          {/* Bonus Table Box */}
          <Box
            sx={{
              width: { xs: "100%", md: "14%" },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(to right, #040124, #710819)",
              borderRight: "1px solid rgba(255,255,255,0.1)",
              p: 1,
              borderRadius: "8px",
              overflow: "hidden",
              ml: 6,
            }}
          >
            <BonusTable cheersNumbers={cheersNumbers} />
          </Box>

          {/* Win Amount Box */}
          <Box
            sx={{
              width: { xs: "100%", md: "17%" },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(to right, #040124, #710819)",
              p: 0,
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              mr: 1,
            }}
          >
            <Box
              component='img'
              src='/derash.png'
              alt='derash'
              sx={{
                height: { xs: "50px", sm: "60px", md: "70px" },
                width: { xs: "180px", sm: "260px", md: "280px" },
                mb: 5,
              }}
            />
            {/* <Box sx={{
              backgroundColor: '#fff',
              // borderRadius: '4px',
              px: 6,
          
              width: { xs: '250px', sm: '280px', md: '300px' },
              height: { xs: '60px', sm: '70px', md: '80px' },
              alignSelf: 'center',
              
            }}>
              <Typography  sx={{ 
                color: '#4a0000',
                fontWeight: 'bolder',
                fontSize: '4px',
                
                
              }}>
                ደራሽ
              </Typography>
            </Box> */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0,
                mt: -16,
                mr: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "5rem",
                  mb: -7,
                  color: "#f0efe7",
                  fontWeight: "bolder",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                {Number(totalBetAmount).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "2.5rem", sm: "3.5rem", md: "5rem" },
                  color: "#f0efe7",
                  fontWeight: "bolder",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  mt: 0,
                }}
              >
                ብር
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Numbers grid */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            backgroundColor: "#1a1a1a",
            mt: -5,
          }}
        >
          <Grid
            container
            spacing={0.2}
            sx={{
              width: "100%",
              margin: 0,
              padding: { xs: "0 8px", sm: "0 12px", md: "0 16px" },
              backgroundColor: "#1a1a1a",
            }}
          >
            {organizedNumbers.map((row, rowIndex) => (
              <Grid item xs={12} key={rowIndex}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    mb: 0.1,
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      minWidth: { xs: 30, sm: 35, md: 40 },
                      fontWeight: "bold",
                      color: "#444444",
                      ml: { xs: 2, sm: 4, md: 6 },
                      width: { xs: 50, sm: 60, md: 70 },
                      height: { xs: 50, sm: 60, md: 70 },
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `url(/bingo.png)`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                      fontSize: "3.5rem",
                    }}
                  >
                    {bingoLetters[rowIndex]}
                  </Typography>
                  <Grid container spacing={0.3} sx={{ flex: 1, m: 0 }}>
                    {row.map((number) => (
                      <Grid
                        item
                        xs={0.65}
                        key={number}
                        sx={{ p: 0.1, mx: 0.3 }}
                      >
                        <Box sx={{ position: "relative" }}>
                          <Paper
                            id={`cell-${number}`}
                            elevation={2}
                            sx={{
                              ml: { xs: 8, sm: 12, md: 16 },
                              p: 1.5,
                              height: { xs: "50px", sm: "60px", md: "70px" },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              ...(currentDrawnNumbers.includes(number)
                                ? {
                                    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%) !important",
                                    boxShadow: "0 0 10px rgba(255, 215, 0, 0.6), inset 0 0 10px rgba(255, 99, 71, 0.3) !important",
                                    border: "2px solid #FFD700 !important"
                                  }
                                : {
                                    background: `linear-gradient(rgba(128, 128, 128, 0.08), rgba(128, 128, 128, 0.08)), url(/normal.png)`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat"
                                  }),
                              backgroundBlendMode: "normal",
                              // Ensure PNG loads properly
                              transition: "background 0.3s ease-in-out",
                              color: currentDrawnNumbers.includes(number)
                                ? "#444444"
                                : "gray",
                              zIndex: 1,
                              fontSize: "3.1rem",
                              fontWeight: "bolder",
                              width: "100%",
                              border: "1px solid rgba(0,0,0,0.1)",
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            {number}
                          </Paper>
                          {(shufflingNumbers.includes(number) ||
                            (currentDrawnNumbers.includes(number) &&
                              currentDrawnNumbers[currentDrawnNumbers.length - 1] ===
                                number)) && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: "128px", // Match the margin-left of the Paper
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "3.1rem",
                                fontWeight: "bolder",
                                zIndex: 999,
                                animation: "beautifulNumberDraw 3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
                                "@keyframes beautifulNumberDraw": {
                                  "0%": {
                                    transform: "scale(1) rotate(0deg)",
                                    background: "linear-gradient(135deg, #ff4444, #ff6666)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 0 rgba(255, 68, 68, 0)",
                                    filter: "brightness(1)",
                                  },
                                  "15%": {
                                    transform: "scale(1.1) rotate(2deg)",
                                    background: "linear-gradient(135deg, #ff3333, #ff5555)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 10px rgba(255, 68, 68, 0.4)",
                                    filter: "brightness(1.1)",
                                  },
                                  "30%": {
                                    transform: "scale(1.3) rotate(-1deg)",
                                    background: "linear-gradient(135deg, #ff2222, #ff4444)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 20px rgba(255, 68, 68, 0.6)",
                                    filter: "brightness(1.2)",
                                  },
                                  "45%": {
                                    transform: "scale(1.5) rotate(1deg)",
                                    background: "linear-gradient(135deg, #ff1111, #ff3333)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 30px rgba(255, 68, 68, 0.8)",
                                    filter: "brightness(1.3)",
                                  },
                                  "60%": {
                                    transform: "scale(1.4) rotate(-0.5deg)",
                                    background: "linear-gradient(135deg, #ff2222, #ff4444)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 25px rgba(255, 68, 68, 0.7)",
                                    filter: "brightness(1.25)",
                                  },
                                  "75%": {
                                    transform: "scale(1.2) rotate(0.5deg)",
                                    background: "linear-gradient(135deg, #ff3333, #ff5555)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 15px rgba(255, 68, 68, 0.5)",
                                    filter: "brightness(1.15)",
                                  },
                                  "85%": {
                                    transform: "scale(1.1) rotate(0deg)",
                                    background: "linear-gradient(135deg, #ff4444, #ff6666)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 10px rgba(255, 68, 68, 0.3)",
                                    filter: "brightness(1.05)",
                                  },
                                  "92%": {
                                    transform: "scale(1.05) rotate(0deg)",
                                    background: "linear-gradient(135deg, #ff5555, #ff7777)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    boxShadow: "0 0 5px rgba(255, 68, 68, 0.2)",
                                    filter: "brightness(1.02)",
                                  },
                                  "96%": {
                                    transform: "scale(1.02) rotate(0deg)",
                                    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%) !important",
                                    boxShadow: "0 0 10px rgba(255, 215, 0, 0.6), inset 0 0 10px rgba(255, 99, 71, 0.3) !important",
                                    border: "2px solid #FFD700 !important",
                                    filter: "brightness(1.1)",
                                  },
                                  "100%": {
                                    transform: "scale(1) rotate(0deg)",
                                    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%) !important",
                                    boxShadow: "0 0 10px rgba(255, 215, 0, 0.6), inset 0 0 10px rgba(255, 99, 71, 0.3) !important",
                                    border: "2px solid #FFD700 !important",
                                    filter: "brightness(1)",
                                    opacity: 0, // Hide overlay to show base element
                                  },
                                },
                              }}
                            >
                              {number}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Bottom controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "0 16px",
              ml: { xs: 2, sm: 10, md: 20 },
              backgroundColor: "#1a1a1a",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2 }}
              alignItems={{ xs: "stretch", sm: "center" }}
              width={{ xs: "100%", sm: "auto" }}
            >
              <Button
                variant='contained'
                // color={isDrawing ? 'success' : 'primary'}
                onClick={toggleDrawing}
                sx={{
                  minWidth: 120,
                  height: 40,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  backgroundColor: isDrawing ? "#4CAF50" : "#790918",
                  "&:hover": {
                    backgroundColor: isDrawing ? "#4CAF50" : "#790918",
                  },
                }}
              >
                {isDrawing ? "STOP" : "BINGO"}
              </Button>
              <Button
                variant='contained'
                onClick={handleShuffle}
                disabled={isShuffling || isDrawing}
                sx={{
                  minWidth: 120,
                  height: 40,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  backgroundColor: "#f0efe7",
                  color: "black",
                }}
              >
                Bowzew
              </Button>

              <Select
                value={selectedCaller}
                onChange={handleCallerChange}
                IconComponent={ArrowDropDown}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "white",
                    },
                  },
                }}
                sx={{
                  minWidth: 100,
                  height: 40,
                  color: "black",
                  backgroundColor: "white",
                  transition: "all 0.2s ease-in-out",
                  willChange: "transform",
                  "& .MuiSelect-select": {
                    py: 1,
                    color: "black",
                    fontWeight: "bold",
                    transition: "color 0.2s ease-in-out",
                  },
                  "& .MuiSelect-icon": {
                    color: "black",
                    transition: "transform 0.2s ease-in-out",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    transition: "border-color 0.2s ease-in-out",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.87)",
                  },
                  "&:hover .MuiSelect-icon": {
                    transform: "rotate(180deg)",
                  },
                }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      transition: "opacity 0.2s ease-in-out",
                      transformOrigin: "top",
                    },
                  },
                }}
              >
                {callers.map((caller) => (
                  <MenuItem
                    key={caller.id}
                    value={caller.id}
                    sx={{
                      color: "black",
                      fontWeight: "bold",
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                      },
                      "&.Mui-selected": {
                        bgcolor: "#e3f2fd",
                        color: "black",
                        "&:hover": {
                          bgcolor: "#bbdefb",
                        },
                      },
                    }}
                  >
                    {caller.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: 300, md: 370 },
                height: { xs: 40, sm: 42, md: 45 },
                ml: 2,
                backgroundColor: "silver",
                position: "relative",
                overflow: "hidden",
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: "#E5E4E2",
                  "& .speed-text": {
                    opacity: 0,
                  },
                },
              }}
              onMouseEnter={() => setIsSliding(true)}
              onMouseLeave={() => setIsSliding(false)}
            >
              {!isSliding && (
                <Typography
                  className='speed-text'
                  variant='caption'
                  sx={{
                    position: "absolute",
                    left: 8,
                    color: "white",
                    zIndex: 2,
                    fontSize: "1rem",
                    transition: "opacity 0.2s",
                  }}
                >
                  play speed: {drawSpeed / 1000}
                </Typography>
              )}
              <Box
                sx={{
                  position: "absolute",
                  height: "100%",
                  width: "7%",
                  backgroundColor: "#01796f",
                  right: `${((drawSpeed / 1000 - 2) / 8) * 100}%`,
                }}
              />
              <Slider
                value={10 - drawSpeed / 1000}
                onChange={handleSpeedChange}
                min={2}
                max={10}
                step={0.5}
                sx={{
                  width: "100%",
                  height: "100%",
                  padding: "0 !important",
                  opacity: 0,
                  position: "absolute",
                  zIndex: 1,
                }}
              />
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 0 }}
              alignItems={{ xs: "stretch", sm: "center" }}
              width={{ xs: "100%", sm: "auto" }}
            >
              <TextField
                inputRef={checkInputRef}
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                onKeyDown={handleCheckInputKeyDown}
                placeholder='Enter Card Number'
                variant='outlined'
                size='small'
                sx={{
                  width: { xs: "100%", sm: 200 },
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    color: "black",
                    placeholderColor: "black",
                  },
                }}
              />
              <Button
                variant='contained'
                onClick={handleCheckCartella}
                sx={{
                  minWidth: 120,
                  height: 40,
                  fontSize: "1rem",
                  backgroundColor: "#790918",
                  // fontWeight: 'bold'
                }}
              >
                Check
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>

      <CartellaCheckModal
        open={showCheckModal}
        onClose={() => setShowCheckModal(false)}
        cartella={checkedCartella}
        cartellaNumber={checkedCartella?.id || ""}
        drawnNumbers={currentDrawnNumbers}
        winningPattern={gamePattern}
        isWinner={checkWin(checkedCartella)}
        onAdditional={() => {
          setShowCheckModal(false);
          // Continue the game
        }}
        onNewBingo={handleNewBingo}
        cheersNumbers={cheersNumbers}
        playSound={playSound}
      />

      {/* Toast Message */}
      <Snackbar
        open={openToast}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity='error'
          onClose={handleCloseToast}
          sx={{
            width: "100%",
            backgroundColor: "#ff0000",
            color: "#ffffff",
            "& .MuiAlert-icon": {
              color: "#ffffff",
            },
            "& .MuiAlert-action": {
              color: "#ffffff",
            },
            fontSize: "1rem",
            alignItems: "center",
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BingoGame;
